import React from 'react';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import { ProgramEnum } from 'src/modules/programs/models/programs';
import * as S from 'src/modules/programs/stepComponents/styles';
import {
    AnswersBySurveysIds,
    SurveyDummyQuestion,
} from 'src/modules/programs/surveys/models/surveys';

import { StepModal } from '../../components/StepModal';
import { fetchSeveralProgramSurveys, submitSeveralProgramSurveys } from '../actions/surveysActions';
import { ProgramSurvey } from '../components/ProgramSurvey';
import { AddSurveyButton } from '../components/styles';
import { GenericProgramSurvey } from '../models/surveysGeneric';
import {
    areCertainProgramSurveysAnswered,
    getAnsweredSurveysAmount,
    getPartiallyAnsweredSurveysAmount,
    getSurveysAnswersBySurveysIds,
} from '../selectors/surveysSelectors';

interface State {
    isModalOpen: boolean;
    wereSurveysAnswered: boolean;
    surveysToShow: number;
}

interface StateProps {
    answers: AnswersBySurveysIds | undefined;
    partiallyAnsweredSurveysAmount: number;
    answeredSurveysAmount: number;
    areSurveysAnswered: boolean;
}

interface ActionsProps {
    fetchSeveralProgramSurveys: typeof fetchSeveralProgramSurveys;
    submitSeveralProgramSurveys: typeof submitSeveralProgramSurveys;
}

interface Props extends StepButtonsProps, StateProps, ActionsProps {
    programId: ProgramEnum;
    surveysIds: GenericProgramSurvey[];
    surveyHeader: string;
    heading?: React.ReactNode;
    postSurvey?: React.ReactNode;
    modalContent?: React.ReactNode;
    noFetch?: boolean;
    questionsHalfWidth?: boolean;
    addButtonText?: string;
    hideUnanswered?: boolean;
    dummyQuestion?: SurveyDummyQuestion;
    showQuestionsIndexes?: boolean;
    validateAllSurveys?: boolean;
}

class SeveralSurveysStepContainer extends React.Component<Props, State> {
    public state: State = {
        isModalOpen: false,
        wereSurveysAnswered: false,
        surveysToShow: 1,
    };

    public static getDerivedStateFromProps = (
        newProps: Props,
        prevState: State,
    ): Partial<State> | null =>
        !prevState.wereSurveysAnswered && newProps.areSurveysAnswered
            ? { wereSurveysAnswered: true }
            : null;

    public componentDidMount(): void {
        const { programId, surveysIds } = this.props;

        this.props.fetchSeveralProgramSurveys({
            programId,
            surveysIds,
        });
    }

    public handleToggleModal = () => {
        this.setState(state => ({ isModalOpen: !state.isModalOpen }));
    };

    private handleSubmit = () => {
        const { programId, answers, goToNextStep } = this.props;

        if (!answers) {
            return;
        }

        this.props.submitSeveralProgramSurveys({
            programId,
            answers,
        });

        goToNextStep();
    };

    private handleAddClick = () =>
        this.setState(state => ({
            surveysToShow: state.surveysToShow + 1,
        }));

    public render(): JSX.Element {
        const showAddButton =
            this.props.hideUnanswered &&
            !this.state.wereSurveysAnswered &&
            this.state.surveysToShow < this.props.surveysIds.length;

        return (
            <>
                <S.ContentRow style={{ flex: 1 }}>
                    <S.ContentColumn>
                        {this.props.heading}
                        {this.props.surveysIds.map((surveyId, index: number) => (
                            <ProgramSurvey
                                key={surveyId}
                                header={`${this.props.surveyHeader} ${index + 1}`}
                                wrapQuestions
                                programId={this.props.programId}
                                surveyId={surveyId}
                                noFetch
                                questionsHalfWidth={this.props.questionsHalfWidth}
                                hideUnanswered={
                                    this.props.hideUnanswered &&
                                    !this.state.wereSurveysAnswered &&
                                    index >= this.state.surveysToShow
                                }
                                postSurvey={this.props.postSurvey}
                                dummyQuestion={this.props.dummyQuestion}
                                showQuestionsIndexes={this.props.showQuestionsIndexes}
                            />
                        ))}
                        {showAddButton && (
                            <AddSurveyButton onClick={this.handleAddClick}>
                                {this.props.addButtonText}
                            </AddSurveyButton>
                        )}
                    </S.ContentColumn>
                </S.ContentRow>
                {this.props.modalContent && (
                    <StepModal
                        isVisible={this.state.isModalOpen}
                        onCancel={this.handleToggleModal}
                        nextStep={this.handleSubmit}
                    >
                        {this.props.modalContent}
                    </StepModal>
                )}
                <StepButtons
                    onPreviousClick={this.props.goToPreviousStep}
                    onNextClick={
                        this.props.modalContent ? this.handleToggleModal : this.handleSubmit
                    }
                    nextDisabled={
                        this.props.validateAllSurveys
                            ? !this.props.areSurveysAnswered
                            : this.props.answeredSurveysAmount < 1
                    }
                    hasNextStep
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState, { programId, surveysIds }: Props): StateProps => ({
    answers: getSurveysAnswersBySurveysIds(state, programId, surveysIds),
    partiallyAnsweredSurveysAmount: getPartiallyAnsweredSurveysAmount(state, programId, surveysIds),
    answeredSurveysAmount: getAnsweredSurveysAmount(state, programId, surveysIds),
    areSurveysAnswered: areCertainProgramSurveysAnswered(state, programId, surveysIds),
});

export const SeveralSurveysStep = connect<StateProps, ActionsProps>(mapStateToProps, {
    fetchSeveralProgramSurveys,
    submitSeveralProgramSurveys,
})(SeveralSurveysStepContainer);
